/**
 * Printing labels for a binder.
 *
 * One connection, many labels. A vendor setting up the night before a show
 * prints a hundred in binder order, and reconnecting between each one would
 * take minutes and drain the printer; niimbluelib's print task is built to hold
 * the link open across pages, so that is what this does.
 *
 * The chosen printer is remembered. Picking a device is a system dialog on iOS
 * and there is no way to skip it, so being asked once per show rather than once
 * per print is the difference between this being usable and not.
 */

import { Capacitor } from '@capacitor/core';
import CardChaseNiimbotClient from './client';
import { renderLabel, renderLabelStack, cardsPerPrint } from './label';

const REMEMBERED_KEY = 'cc:labelPrinter:device';

let client = null;

/**
 * The right transport for where we are running.
 *
 * The web one is imported lazily so its chunk is never fetched on a phone,
 * where the native plugin is used instead.
 */
async function makeClient() {
  if (Capacitor.isNativePlatform()) return new CardChaseNiimbotClient();
  const { NiimbotBluetoothClient } = await import('@mmote/niimbluelib/dist/cjs/client/bluetooth_impl');
  return new NiimbotBluetoothClient();
}

/**
 * Every service a NIIMBOT might talk on.
 *
 * Web Bluetooth will not let a page touch a service it did not declare up
 * front, and the service a given model uses is only discoverable after
 * connecting - so the candidates are all named here.
 */
const NIIMBOT_SERVICES = [
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '000018f0-0000-1000-8000-00805f9b34fb',
  '0000fee7-0000-1000-8000-00805f9b34fb',
];

/**
 * Ask the browser for a printer, showing everything.
 *
 * The library filters the chooser by name prefix and by service uuid, and both
 * are unreliable here: these printers do not advertise their service at all, so
 * that half of the filter can never match, and the name half only matches
 * models already in its library. The result is an empty chooser and no way to
 * tell whether the printer is off, busy or simply unrecognised.
 *
 * Showing every device puts that judgement where it belongs. The person can see
 * their printer by name and pick it.
 */
async function chooseWebDevice() {
  return navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: NIIMBOT_SERVICES,
  });
}

/** The printer chosen last time, if any. */
export function rememberedPrinter() {
  try {
    const raw = window.localStorage.getItem(REMEMBERED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function remember(device) {
  try {
    window.localStorage.setItem(REMEMBERED_KEY, JSON.stringify(device));
  } catch {
    /* a remembered printer is a convenience, never a requirement */
  }
}

export function forgetPrinter() {
  try {
    window.localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Whether this device can talk to a printer at all.
 *
 * Two ways in. On a phone it is the native Bluetooth plugin. On a desktop it is
 * Web Bluetooth, which Chrome and Edge have and Safari and Firefox do not - so
 * a vendor can set up a show from a laptop, and so this can be tested without
 * waiting on a native build.
 */
export function printingSupported() {
  if (Capacitor.isNativePlatform()) return true;
  return typeof navigator !== 'undefined' && typeof navigator.bluetooth?.requestDevice === 'function';
}

/** Which of the two we would use, for wording that has to differ. */
export function printingTransport() {
  if (Capacitor.isNativePlatform()) return 'native';
  if (typeof navigator !== 'undefined' && navigator.bluetooth) return 'web';
  return null;
}

export function isConnected() {
  return Boolean(client?.isConnected());
}

/**
 * Connect, opening the system picker unless a printer is already remembered.
 * @param {object} [options]
 * @param {boolean} [options.pickAgain] ignore the remembered one and choose.
 */
export async function connect({ pickAgain = false } = {}) {
  if (!printingSupported()) {
    throw new Error('This browser has no Bluetooth. Use the app on your phone, or Chrome on a computer.');
  }
  if (client?.isConnected()) return client.getPrinterInfo();

  client = await makeClient();
  const saved = pickAgain ? null : rememberedPrinter();

  // On the web the chooser is ours, so it can show every device rather than
  // only the ones the library's filters recognise.
  const webDevice = Capacitor.isNativePlatform() ? null : await chooseWebDevice();

  let info;
  try {
    info = webDevice
      ? await client.connect({ authorizedDevice: webDevice })
      : await client.connect(saved
        ? { deviceId: saved.deviceId, deviceName: saved.name }
        : {});
  } catch (err) {
    // A remembered printer is kept when it fails to answer.
    //
    // This used to forget it and open the picker instead, on the theory that
    // getting straight to a list beats being told to go and find a settings
    // screen. That was wrong twice over: a printer that does not answer is
    // almost always asleep or out of range rather than gone, and the picker it
    // dropped into is a list of unnamed devices that is nearly impossible to
    // choose from. One failed tap threw away a working pairing.
    //
    // So the pairing survives and the message says what to do. Print settings
    // still has Forget for a printer that really has been replaced.
    if (saved && !webDevice) {
      client = await makeClient();
      throw new Error(
        `${saved.name || 'That printer'} did not answer. Turn it on and close the `
        + 'NIIMBOT app, then try again. Still nothing: Print settings, Forget, '
        + 'and link it again.',
      );
    }
    throw err;
  }

  remember({ deviceId: info.deviceId, name: info.deviceName });
  return client.getPrinterInfo();
}

export async function disconnect() {
  if (client) await client.disconnect();
  client = null;
}

/** The model the printer says it is, rather than what the box said. */
export function printerModel() {
  return client?.getModelMetadata()?.model || null;
}

/**
 * Print a run of labels, in the order given.
 *
 * @param {object[]} cards         collection rows, already in binder order
 * @param {object} options
 * @param {string[]} options.fields    which fields to draw
 * @param {object} options.size        one of LABEL_SIZES
 * @param {number} [options.density]   1-5 on these models; 3 is the default
 * @param {function} [options.onProgress] (done, total, card)
 * @param {function} [options.qrUrlFor]  (card) => url, when the QR field is on
 * @param {function} [options.shouldStop] () => boolean, checked between labels
 */
export async function printLabels(cards, {
  fields, size, density = 3, onProgress, qrUrlFor, shouldStop,
} = {}) {
  if (!client?.isConnected()) throw new Error('The printer is not connected.');
  const list = (cards || []).filter(Boolean);
  if (list.length === 0) return { printed: 0 };

  // The task differs by model - a B1 and a B21 do not print the same way - and
  // the library picks the right one from what the printer reported at connect.
  const taskName = client.getPrintTaskType();
  if (!taskName) throw new Error('That printer model is not supported yet.');

  const task = client.protocol.newPrintTask(taskName, {
    totalPages: list.length,
    density,
  });

  const { ImageEncoder } = await import('@mmote/niimbluelib/dist/cjs/image_encoder');
  // PageColorType is an enum, not a word. SingleColor is 0 and is what a B1
  // does: one thermal head, ink or no ink. Passing a string here silently
  // encodes as the wrong page type.
  const { PageColorType } = await import('@mmote/niimbluelib/dist/cjs/packets/enumerations');

  let printed = 0;
  try {
    await task.printInit();
    // A paired roll is one print per N cards, not per card. The printer's gap
    // sensor reads the pair as a single label, so sending one card at a time
    // burns the blank half of every pair.
    const perPrint = cardsPerPrint(size);

    for (let i = 0; i < list.length; i += perPrint) {
      if (shouldStop?.()) break;
      const batch = list.slice(i, i + perPrint);

      // eslint-disable-next-line no-await-in-loop
      const canvas = perPrint > 1
        ? await renderLabelStack(batch, { fields, size, qrUrlFor })
        : await renderLabel(batch[0], {
          fields,
          size,
          qrUrl: qrUrlFor ? qrUrlFor(batch[0]) : null,
        });

      const encoded = ImageEncoder.encodeCanvas(canvas, PageColorType.SingleColor, 'top');
      // eslint-disable-next-line no-await-in-loop
      await task.printPage(encoded, 1);
      // eslint-disable-next-line no-await-in-loop
      await task.waitForPageFinished();
      // Cards, not prints: the progress line counts what the person selected.
      printed += batch.length;
      onProgress?.(printed, list.length, batch[batch.length - 1]);
    }
    await task.waitForFinished();
  } finally {
    // Always, even on failure: a printer left mid-job ignores the next one.
    await client.protocol.printEnd().catch(() => {});
  }

  return { printed };
}

export {
  LABEL_FIELDS, DEFAULT_FIELDS, LABEL_SIZES, DEFAULT_SIZE_KEY,
  renderLabel, renderLabelStack, cardsPerPrint,
} from './label';
export {
  readPrefs, writePrefs, sizeForKey, labelPrefs,
} from './prefs';
